'use client'

import { ProductCard } from '@/feature/product/components/product-card/ProductCard.client'
import { useProductsListFavoriteStatus } from '@/feature/product/hooks/useProductsListFavoriteStatus'
import { useVisibleProductCards } from '@/feature/product/hooks/useVisibleProductCards'
import { filterAvailableProducts } from '@/feature/product/services/product.filtering'
import { APOLLO_CLIENT_SIDE_OPTIONS } from '@/lib/apollo/clients/apolloClient.client'
import { twMerge } from '@/lib/classname/twMerge'
import { useTranslation } from '@/lib/i18n/useTranslation.client'
import { isSSR } from '@/lib/next/services/nextjs.rendering'
import { Badge } from '@/ui/badge/Badge'
import { CSSSlider } from '@/ui/layouts/css-slider/CSSSlider.client'
import { Section } from '@/ui/layouts/section/Section'
import { DefinitionList } from '@/ui/lists/definition-list/DefinitionList.client'
import { DefinitionListProvider } from '@/ui/lists/definition-list/DefinitionListProvider.client'
import { useQuery } from '@apollo/client'
import { gql } from '@gql-generated/gql'
import {
  GetEquivalentProductsDocument,
  GetEquivalentProductsPriceAndStockDocument,
  ProductBuyBoxPriceAndStockDocument,
  type ProductCardFragment,
  ProductType,
} from '@gql-generated/graphql'
import { useEffect, useMemo, useRef } from 'react'
import { EquivalentProductSeeMoreButton } from './EquivalentProductSeeMoreButton.client'

gql(/* GraphQL */ `
  fragment EquivalentProductFieldsWithoutPrice on Product {
    name
    urlKey
    reference
    brand {
      logo {
        ...FullPicture
      }
      name
      urlKey
    }
    mainImage {
      ...FullPicture
    }
    type
    technicalSpecs {
      label
      value
    }
  }
`)

gql(/* GraphQL */ `
  query GetEquivalentProducts($urlKey: String!) {
    product(urlKey: $urlKey) {
      type
      reference
      ...EquivalentProductFieldsWithoutPrice
      equivalentProducts {
        ...EquivalentProductFieldsWithoutPrice
      }
    }
  }
`)

gql(/* GraphQL */ `
  query GetEquivalentProductsPriceAndStock($references: [String!]!) {
    productsByReferences(references: $references) {
      ...ProductCard_ProductPriceAndStock
    }
  }
`)

export function EquivalentProductSlider({ slug }: { slug: string }) {
  const { t } = useTranslation(['generic', 'productPage'])

  const { data } = useQuery(GetEquivalentProductsDocument, {
    ...APOLLO_CLIENT_SIDE_OPTIONS,
    variables: {
      urlKey: slug,
    },
    fetchPolicy: 'cache-first',
    nextFetchPolicy: 'cache-first',
  })

  const { data: priceAndStockData } = useQuery(ProductBuyBoxPriceAndStockDocument, {
    ...APOLLO_CLIENT_SIDE_OPTIONS,
    variables: { reference: data?.product?.reference || '' },
    skip: !data?.product?.reference || isSSR,
  })

  const references = data?.product?.equivalentProducts?.map((product) => product.reference) || []

  const articlesReferences = useMemo(
    () =>
      data?.product?.equivalentProducts
        ?.map((product) => (product.type === ProductType.Article ? product.reference : undefined))
        .filter((reference) => reference !== undefined) || [],
    [data?.product?.equivalentProducts],
  )
  if (data?.product?.reference && data?.product?.type === ProductType.Article)
    articlesReferences.push(data?.product?.reference)

  const { data: pricesAndStocks, loading: pricesAndStocksLoading } = useQuery(
    GetEquivalentProductsPriceAndStockDocument,
    {
      ...APOLLO_CLIENT_SIDE_OPTIONS,
      variables: { references },
      skip: !data?.product || references.length === 0 || isSSR,
    },
  )

  const { data: favoriteMap } = useProductsListFavoriteStatus(articlesReferences)

  const mainProduct = data?.product

  const analyticsSource =
    mainProduct?.type === ProductType.Article
      ? 'produits équivalents - fiche article'
      : 'produits équivalents - fiche produit'
  const equivalentProductCardsRefs = useRef<React.RefObject<HTMLDivElement | null>[]>([])
  const equivalentProducts = filterAvailableProducts(
    mainProduct?.equivalentProducts?.map((product) => {
      const priceAndStockData = pricesAndStocks?.productsByReferences?.find(
        (priceAndStock) => priceAndStock.reference === product.reference,
      )

      return {
        ...product,
        stock: {
          availability: priceAndStockData?.stock?.availability ?? [],
        },
        analytics: {
          promotionStatus: priceAndStockData?.analytics?.promotionStatus,
          profileCase: priceAndStockData?.analytics?.profileCase,
        },
      }
    }) || [],
  )
  const { setProductCardsRefs, productCardsRefs: productCardsRefsHook } = useVisibleProductCards({
    itemListName: analyticsSource,
    products: equivalentProducts as ProductCardFragment[],
  })

  useEffect(() => {
    equivalentProductCardsRefs.current = equivalentProducts.map(
      (_, index) => equivalentProductCardsRefs.current[index] || { current: null },
    )
  }, [equivalentProducts])

  useEffect(() => {
    if (
      equivalentProductCardsRefs.current.length > 0 &&
      productCardsRefsHook.length !== equivalentProductCardsRefs.current.length
    ) {
      setProductCardsRefs(equivalentProductCardsRefs.current)
    }
  }, [equivalentProducts, setProductCardsRefs, productCardsRefsHook.length])

  if (!mainProduct?.equivalentProducts || !mainProduct?.equivalentProducts.length) return null
  if (!mainProduct?.technicalSpecs || !equivalentProducts.length) return null

  const technicalSpecs = mainProduct.technicalSpecs.flatMap(({ label, value }) =>
    label ? [{ label, value }] : [],
  )

  const referenceValuesByLabel = Object.fromEntries(
    technicalSpecs.map(({ label, value }) => [label, value ?? '']),
  )

  const mainProductCard = (
    <div className="w-[16rem]">
      <ProductCard
        noQuickAddToCart
        className="border-1.5 border-primary"
        product={
          {
            ...mainProduct,
            ...priceAndStockData?.productByReference,
          } as ProductCardFragment
        }
        productPriceAndStock={{
          priceAndStockData: pricesAndStocks?.productsByReferences?.find(
            (priceAndStock) => priceAndStock.reference === mainProduct.reference,
          ),
          priceAndStockIsLoading: pricesAndStocksLoading,
        }}
        isFavorite={favoriteMap?.[mainProduct.reference] || false}
        restreintWidth
        bottomNode={
          <DefinitionList aria-label={t('productPage:technicalSpecs.title')}>
            {technicalSpecs.map(({ label, value }) => (
              <DefinitionList.Item key={label} label={label}>
                {value || ''}
              </DefinitionList.Item>
            ))}
          </DefinitionList>
        }
        customTag={
          <Badge tint="brand" size="sm">
            {t('productPage:equivalentProductsSliderSection.customTagLabel')}
          </Badge>
        }
        source={analyticsSource}
      />
    </div>
  )

  return (
    <DefinitionListProvider>
      <Section title={t('productPage:equivalentProductsSliderSection.title')}>
        <div className="flex flex-row">
          <div className="hidden shrink-0 flex-row desktop:flex">
            <div className="flex h-full w-[13rem] shrink-0 flex-col justify-end p-2">
              {technicalSpecs.length > 6 && <EquivalentProductSeeMoreButton />}
              <DefinitionList aria-label={t('productPage:technicalSpecs.title')}>
                {technicalSpecs.map(({ label }) => (
                  <DefinitionList.Item key={label}>{label}</DefinitionList.Item>
                ))}
              </DefinitionList>
            </div>
            {mainProductCard}
          </div>
          <div className="w-0 grow">
            <CSSSlider gap="sm">
              {!!mainProduct && <div className="block desktop:hidden">{mainProductCard}</div>}

              {equivalentProducts.map((equivalentProduct, index) => {
                // Some equivalent products may not have the same technical specs as the main product.
                // Filter the technical specs to only show the ones that are present in the main product.
                const filteredTechnicalSpecs = (equivalentProduct.technicalSpecs || []).filter(
                  ({ label }) => technicalSpecs.some((spec) => spec.label === label),
                )

                // Add the missing technical specs that are present in the main product but not in
                // the equivalent product.
                const missingTechnicalSpecs = technicalSpecs
                  .filter(
                    ({ label }) => !filteredTechnicalSpecs.some((spec) => spec.label === label),
                  )
                  .map(({ label }) => ({
                    label,
                    value: '-',
                  }))

                if (missingTechnicalSpecs.length) {
                  filteredTechnicalSpecs.push(...missingTechnicalSpecs)

                  // Sort the technical specs to match the order of the main product.
                  filteredTechnicalSpecs.sort((a, b) => {
                    const indexA = technicalSpecs.findIndex((spec) => spec.label === a.label)
                    const indexB = technicalSpecs.findIndex((spec) => spec.label === b.label)

                    return indexA - indexB
                  })
                }

                const labeledTechnicalSpecs = filteredTechnicalSpecs.flatMap(({ label, value }) =>
                  label ? [{ label, value }] : [],
                )

                return (
                  <div
                    key={equivalentProduct.urlKey}
                    className={twMerge('w-[16rem]', index === 0 && 'pl-1')}
                  >
                    <ProductCard
                      noQuickAddToCart
                      ref={equivalentProductCardsRefs.current[index]}
                      product={equivalentProduct as ProductCardFragment}
                      productPriceAndStock={{
                        priceAndStockData: pricesAndStocks?.productsByReferences?.find(
                          (priceAndStock) =>
                            priceAndStock.reference === equivalentProduct.reference,
                        ),
                        priceAndStockIsLoading: pricesAndStocksLoading,
                      }}
                      isFavorite={favoriteMap?.[equivalentProduct.reference] || false}
                      restreintWidth
                      bottomNode={
                        <DefinitionList aria-label={t('productPage:technicalSpecs.title')}>
                          {labeledTechnicalSpecs.map(({ label, value }) => (
                            <DefinitionList.Item
                              key={label}
                              label={label}
                              isBold={(value || '') !== (referenceValuesByLabel[label] ?? '')}
                            >
                              {value || ''}
                            </DefinitionList.Item>
                          ))}
                        </DefinitionList>
                      }
                      position={index + 1}
                    />
                  </div>
                )
              })}
            </CSSSlider>
          </div>
        </div>
      </Section>
    </DefinitionListProvider>
  )
}
